import { ActionIcon, TextInput } from '@mantine/core';
import { IconSearch, IconX } from '@tabler/icons-react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface SearchBoxProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  placeholder?: string;
  width?: number;
}

const SearchBox = memo(({ value, onChange, onClear, placeholder, width = 300 }: SearchBoxProps) => {
  const { t } = useTranslation();
  const isComposingRef = useRef(false);
  // 本地 state 控制输入框显示，确保 IME 组合期间输入正常显示
  const [localValue, setLocalValue] = useState(value);

  // 父组件 value 变化时同步到本地（如外部清除搜索）
  useEffect(() => {
    if (!isComposingRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;
      setLocalValue(newValue);
      // IME 组合输入期间只更新显示，不通知父组件触发搜索
      if (!isComposingRef.current) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  const handleCompositionStart = useCallback(() => {
    isComposingRef.current = true;
  }, []);

  const handleCompositionEnd = useCallback(
    (e: React.CompositionEvent<HTMLInputElement>) => {
      isComposingRef.current = false;
      // 组合输入结束后，使用最终确认的值通知父组件
      onChange(e.currentTarget.value);
    },
    [onChange]
  );

  return (
    <TextInput
      placeholder={placeholder || t('common.search_bots', 'Search bots...')}
      leftSection={<IconSearch size={16} />}
      rightSection={
        localValue ? (
          <ActionIcon variant="subtle" onClick={onClear} size="sm">
            <IconX size={14} />
          </ActionIcon>
        ) : null
      }
      value={localValue}
      onChange={handleChange}
      onCompositionStart={handleCompositionStart}
      onCompositionEnd={handleCompositionEnd}
      style={{ width }}
    />
  );
});

SearchBox.displayName = 'SearchBox';

export default SearchBox;
